/**
 * @description
 * User service for managing client users and roles.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { 
  ClientUser, 
  CreateUserRequest, 
  UpdateUserRequest, 
  PaginationInfo,
  PlatformSyncResult,
  PlatformSyncInfo 
} from "./types";

/**
 * User service for managing client users
 */
export class UserService extends BaseAPIClient {
  /**
   * Get all users from the admin database
   */
  async getUsers(): Promise<Array<{
    id: string;
    email: string;
    name: string;
    provider: string;
    role: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>> {
    const data = await this.request<{ users: Array<{
      id: string;
      email: string;
      name: string;
      provider: string;
      role: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    }> }>("/users");
    return data.users;
  }

  /**
   * Get all users for a client
   */
  async getClientUsers(
    clientId: string,
    options?: {
      page?: number;
      limit?: number;
      role?: string;
      status?: string;
      search?: string;
    },
  ): Promise<{ users: ClientUser[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.role) params.set("role", options.role);
    if (options?.status) params.set("status", options.status);
    if (options?.search) params.set("search", options.search);

    const queryString = params.toString();
    const endpoint = `/clients/${clientId}/users${queryString ? `?${queryString}` : ""}`;

    // Backend returns { data: [], pagination: {} } but we need to transform to { users: [], pagination: {} }
    const response = await this.request<{ data: ClientUser[]; pagination: PaginationInfo }>(
      endpoint,
    );
    
    return {
      users: response.data || [],
      pagination: response.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
      },
    };
  }

  /**
   * Add user to client
   */
  async addClientUser(
    clientId: string,
    userData: CreateUserRequest,
  ): Promise<{ user: ClientUser; platformSync?: PlatformSyncInfo }> {
    return this.request<{ user: ClientUser; platformSync?: PlatformSyncInfo }>(
      `/clients/${clientId}/users`,
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
    );
  }

  /**
   * Update user
   */
  async updateClientUser(
    clientId: string,
    userId: string,
    updates: UpdateUserRequest,
  ): Promise<{ user: ClientUser }> {
    return this.request<{ user: ClientUser }>(
      `/clients/${clientId}/users/${userId}`,
      {
        method: "PUT",
        body: JSON.stringify(updates),
      },
    );
  }

  /**
   * Delete user from client
   */
  async deleteClientUser(
    clientId: string,
    userId: string,
  ): Promise<{ message: string; deletedApiKeys: number }> {
    return this.request<{ message: string; deletedApiKeys: number }>(
      `/clients/${clientId}/users/${userId}`,
      {
        method: "DELETE",
      },
    );
  }

  /**
   * Sync users from platform
   */
  async syncUsersFromPlatform(
    clientId: string,
    options?: {
      forceSync?: boolean;
      syncApiKeys?: boolean;
    },
  ): Promise<{
    syncResult: PlatformSyncResult;
    platformInfo: { lastSyncAt: string; platformClientId: string };
  }> {
    return this.request<{
      syncResult: PlatformSyncResult;
      platformInfo: { lastSyncAt: string; platformClientId: string };
    }>(`/clients/${clientId}/users/sync`, {
      method: "POST",
      body: JSON.stringify(options || {}),
    });
  }

  /**
   * Update a user's role and assigned API key
   */
  async updateUserRoleAndKey(
    clientId: string,
    userId: string,
    data: { role: string; apiKeyId?: string }
  ): Promise<{ success: boolean; message: string }> {
    return this.request(`/clients/${clientId}/users/${userId}/role-and-key`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

// Export singleton instance
export const userService = new UserService();
